/**
 * Undo/redo (SPEC §6.4): a stack of transactions, each holding the inverse ops computed when it
 * was applied. One transaction = one undo unit (a split, a drag of twelve blocks, an AI job).
 *
 * Redo replays the inverse of the undo, not the original ops: undoing a create leaves a tombstone,
 * so redo must be a restore, not a second create.
 */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import type { OpBody } from './ops.ts'
import type { Caret } from './structural.ts'

export interface TxnMeta {
  readonly label?: string
  readonly caretBefore?: Caret | null
  readonly caretAfter?: Caret | null
  readonly rootBefore?: NodeId
  readonly rootAfter?: NodeId
}

export interface Txn extends TxnMeta {
  /** Forward ops, as applied. */
  readonly ops: readonly OpBody[]
  /** Inverse of each op, in application order. */
  inverse: readonly OpBody[]
  /** Sync transaction id, when applied through a SyncEngine (see Applier). */
  id?: string
}

/** How ops reach the graph: directly, or through a SyncEngine (which also queues them for the server). */
export type Applier = (ops: readonly OpBody[], ts: number) => { inverse: OpBody[]; id?: string }

interface Pending extends TxnMeta {
  readonly ops: readonly OpBody[]
}

export class History {
  private undoStack: Txn[] = []
  private redoStack: Pending[] = []

  readonly limit: number
  private readonly applier: Applier | undefined

  constructor(limit = 200, applier?: Applier) {
    this.limit = limit
    this.applier = applier
  }

  private run(g: Graph, ops: readonly OpBody[], ts: number): { inverse: OpBody[]; id?: string } {
    return this.applier ? this.applier(ops, ts) : { inverse: g.applyBatch(ops, ts) }
  }

  /** A pending sync transaction was rebased: keep its undo inverse exact. */
  updateInverse(id: string, inverse: readonly OpBody[]): void {
    for (let i = this.undoStack.length - 1; i >= 0; i--) {
      const t = this.undoStack[i]!
      if (t.id === id) {
        t.inverse = inverse
        return
      }
    }
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0
  }

  get depth(): number {
    return this.undoStack.length
  }

  /** Apply `ops` atomically as one undo unit. Returns null for an empty op list. */
  commit(g: Graph, ops: readonly OpBody[], ts: number, meta: TxnMeta = {}): Txn | null {
    if (ops.length === 0) return null
    const { inverse, id } = this.run(g, ops, ts)
    const t: Txn = { ...meta, ops, inverse, id }
    this.push(t)
    this.redoStack = []
    return t
  }

  undo(g: Graph, ts: number): Txn | null {
    const t = this.undoStack.pop()
    if (!t) return null
    let applied: OpBody[]
    try {
      applied = this.run(g, [...t.inverse].reverse(), ts).inverse
    } catch (e) {
      this.undoStack.push(t)
      throw e
    }
    this.redoStack.push({ ...metaOf(t), ops: applied.reverse() })
    return t
  }

  redo(g: Graph, ts: number): Txn | null {
    const p = this.redoStack.pop()
    if (!p) return null
    let result: { inverse: OpBody[]; id?: string }
    try {
      result = this.run(g, p.ops, ts)
    } catch (e) {
      this.redoStack.push(p)
      throw e
    }
    const t: Txn = { ...metaOf(p), ops: p.ops, inverse: result.inverse, id: result.id }
    this.push(t)
    return t
  }

  clear(): void {
    this.undoStack = []
    this.redoStack = []
  }

  private push(t: Txn): void {
    this.undoStack.push(t)
    if (this.undoStack.length > this.limit) this.undoStack.shift()
  }
}

function metaOf(t: TxnMeta): TxnMeta {
  const { label, caretBefore, caretAfter, rootBefore, rootAfter } = t
  return { label, caretBefore, caretAfter, rootBefore, rootAfter }
}
